import React from "react";
import { Ban } from "lucide-react";

/**
 * The agent's colour code for a Database row — the same idea as colouring a cell in
 * a spreadsheet (Christian Gravias, 2026-08-05). Colour NAMES are stored, not hex,
 * so the palette can be restyled here without touching the data.
 *
 * `dot` is the solid swatch and the row's left edge. `rowCls` paints the WHOLE line
 * in that colour (Christian, 2026-08-10: "make it where the colours actually colour
 * code the whole line", with a screenshot of a solid green row) and `rowSelCls` is
 * the same colour a shade deeper for a row that's also ticked for a bulk action.
 *
 * The tints are deliberately strong enough to read at rest on a white grid. The
 * first version was ~2% off white and got reported as "the colour isn't working".
 * They are WHOLE literal class strings, never built by string concatenation, so
 * Tailwind's file scanner still emits the rules, and they stay classes (not inline
 * styles) so the `hover:` half keeps working.
 */
export const COLOURS = [
  { key: "red", label: "Red", dot: "#e5484d", rowCls: "bg-[#f8c8ca] hover:bg-[#f2b0b3]", rowSelCls: "bg-[#f2b0b3] hover:bg-[#f2b0b3]" },
  { key: "orange", label: "Orange", dot: "#f07c26", rowCls: "bg-[#fbdcc0] hover:bg-[#f8caa2]", rowSelCls: "bg-[#f8caa2] hover:bg-[#f8caa2]" },
  { key: "yellow", label: "Yellow", dot: "#e3b427", rowCls: "bg-[#fbeeb5] hover:bg-[#f7e492]", rowSelCls: "bg-[#f7e492] hover:bg-[#f7e492]" },
  { key: "green", label: "Green", dot: "#34b56a", rowCls: "bg-[#c3ebd3] hover:bg-[#aee2c1]", rowSelCls: "bg-[#aee2c1] hover:bg-[#aee2c1]" },
  { key: "teal", label: "Teal", dot: "#55c5d0", rowCls: "bg-[#bfe9ee] hover:bg-[#a5e0e7]", rowSelCls: "bg-[#a5e0e7] hover:bg-[#a5e0e7]" },
  { key: "blue", label: "Blue", dot: "#2f9bd6", rowCls: "bg-[#c5e2f6] hover:bg-[#a9d4f1]", rowSelCls: "bg-[#a9d4f1] hover:bg-[#a9d4f1]" },
  { key: "purple", label: "Purple", dot: "#8b5cf6", rowCls: "bg-[#ddd0fb] hover:bg-[#cbb8f8]", rowSelCls: "bg-[#cbb8f8] hover:bg-[#cbb8f8]" },
  { key: "pink", label: "Pink", dot: "#e35d9c", rowCls: "bg-[#f8ccdf] hover:bg-[#f4b4d0]", rowSelCls: "bg-[#f4b4d0] hover:bg-[#f4b4d0]" },
  { key: "brown", label: "Brown", dot: "#a1662f", rowCls: "bg-[#e8d5c0] hover:bg-[#dcc2a5]", rowSelCls: "bg-[#dcc2a5] hover:bg-[#dcc2a5]" },
  { key: "grey", label: "Grey", dot: "#8b98a5", rowCls: "bg-[#dee2e6] hover:bg-[#ced4da]", rowSelCls: "bg-[#ced4da] hover:bg-[#ced4da]" },
];

const BY_KEY = COLOURS.reduce((m, c) => ({ ...m, [c.key]: c }), {});

export const colourOf = (key) => BY_KEY[key] || null;

/**
 * The swatch grid. Presentational: the caller owns the open/close state and does
 * the saving, so the same palette serves a single row and the bulk bar.
 */
export default function ColourPicker({ value, onPick, onClose, align = "left", drop = "down" }) {
  return (
    <>
      {/* Click-away catcher, so picking a colour never also opens the row drawer. */}
      <div
        className="fixed inset-0 z-40"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        className={`absolute z-50 ${drop === "up" ? "bottom-full mb-1" : "top-full mt-1"}
          ${align === "right" ? "right-0" : "left-0"}
          bg-white border border-gray-200 rounded-xl shadow-xl p-2.5 w-[184px]`}
      >
        <div className="grid grid-cols-5 gap-1.5">
          {COLOURS.map((c) => (
            <button
              key={c.key}
              title={c.label}
              onClick={() => onPick(c.key)}
              className={`w-7 h-7 rounded-full border-2 ${
                value === c.key ? "border-gray-900" : "border-transparent"
              }`}
              style={{ background: c.dot }}
            />
          ))}
        </div>
        <button
          onClick={() => onPick("")}
          className="mt-2 w-full flex items-center justify-center gap-1.5 text-[11px] font-bold text-gray-500 border border-gray-200 rounded-lg py-1.5 hover:bg-gray-50"
        >
          <Ban size={12} /> No colour
        </button>
      </div>
    </>
  );
}
